<template>
  <div class="confirm-page">
    <div class="confirm-container">
      <div class="confirm-card">
        <!-- Tenant Logo -->
        <div class="confirm-logo">
          <img
            v-if="tenantLogo"
            :src="tenantLogo"
            :alt="tenantName ?? ''"
            class="logo-image"
          />
          <div v-else class="logo-placeholder">
            {{ tenantName || 'Franchise Factory' }}
          </div>
        </div>

        <div class="confirm-header">
          <h1>{{ t('Confirm Account') }}</h1>
          <p class="subtitle">{{ t('Enter the verification code sent to your email') }}</p>
        </div>

        <form @submit.prevent="handleConfirm" class="confirm-form">
          <!-- Error Message -->
          <div v-if="errorMessage" class="alert alert-error">
            {{ errorMessage }}
          </div>

          <!-- Success Message -->
          <div v-if="successMessage" class="alert alert-success">
            {{ successMessage }}
          </div>

          <!-- Username Field (read-only if from query) -->
          <div class="form-group">
            <label for="username">{{ t('Username') }}</label>
            <input
              id="username"
              v-model="username"
              type="text"
              :placeholder="t('Enter your username')"
              required
              autocomplete="username"
              :disabled="isLoading || confirmationSuccess"
              data-testid="auth-confirm-username"
            />
          </div>

          <!-- Verification Code Field -->
          <div class="form-group">
            <label for="code">{{ t('Verification Code') }}</label>
            <input
              id="code"
              v-model="verificationCode"
              type="text"
              :placeholder="t('Enter verification code')"
              required
              maxlength="6"
              pattern="[0-9]{6}"
              autocomplete="one-time-code"
              :disabled="isLoading || confirmationSuccess"
              data-testid="auth-confirm-code"
            />
            <small class="help-text">{{ t('6-digit code from your email') }}</small>
          </div>

          <!-- Submit Button -->
          <button
            v-if="!confirmationSuccess"
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="isLoading"
            data-testid="auth-confirm-submit"
          >
            <span v-if="!isLoading">{{ t('Confirm Account') }}</span>
            <span v-else>{{ t('Confirming...') }}</span>
          </button>

          <!-- Go to Login Button -->
          <button
            v-else
            type="button"
            class="btn btn-success btn-block"
            @click="goToLogin"
            data-testid="auth-confirm-go-to-login"
          >
            {{ t('Continue to Login') }}
          </button>
        </form>

        <!-- Resend Code Link -->
        <div v-if="!confirmationSuccess" class="confirm-footer">
          <p>
            {{ t("Didn't receive the code?") }}
            <a href="#" class="resend-link" @click.prevent="resendCode" data-testid="auth-confirm-resend-code">
              {{ t('Resend Code') }}
            </a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { tenantConfig } from '@/services/tenantConfigService'
import { log } from '@/utils/DebugLogger'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { confirmAccount, resendConfirmationCode } = useAuth()

// Tenant branding
const tenantLogo = computed<string | null>(() => tenantConfig.get<string>('logo_url'))
const tenantName = computed<string | null>(() => tenantConfig.get<string>('tenant_name'))
const primaryColor = computed<string>(() => tenantConfig.get<string>('primary_color') || '#667eea')
const secondaryColor = computed<string>(() => tenantConfig.get<string>('secondary_color') || '#764ba2')

// Form state
const username = ref<string>('')
const verificationCode = ref<string>('')
const isLoading = ref<boolean>(false)
const errorMessage = ref<string>('')
const successMessage = ref<string>('')
const confirmationSuccess = ref<boolean>(false)

onMounted(() => {
  // Pre-fill username from query params if available
  const queryUsername = route.query.username
  if (typeof queryUsername === 'string') {
    username.value = queryUsername
  }

  log('ConfirmPage', 'onMounted', 'Confirm page loaded', {
    username: username.value,
    redirectTo: route.query.redirectTo
  })
})

/**
 * Handle confirmation form submission
 */
async function handleConfirm(): Promise<void> {
  try {
    isLoading.value = true
    errorMessage.value = ''
    successMessage.value = ''

    log('ConfirmPage', 'handleConfirm', 'Attempting confirmation', {
      username: username.value,
      code: verificationCode.value
    })

    const result = await confirmAccount(username.value, verificationCode.value)

    if (result.success) {
      log('ConfirmPage', 'handleConfirm', '✅ Confirmation successful')

      confirmationSuccess.value = true
      successMessage.value = t('Account confirmed successfully! You can now log in.')
    } else {
      log('ConfirmPage', 'handleConfirm', '❌ Confirmation failed:', result.error)
      errorMessage.value = result.error || t('Invalid verification code')
    }
  } catch (error) {
    log('ConfirmPage', 'handleConfirm', '❌ Confirmation error:', error)
    errorMessage.value = t('An error occurred. Please try again.')
  } finally {
    isLoading.value = false
  }
}

/**
 * Resend verification code
 */
async function resendCode(): Promise<void> {
  if (!username.value) {
    errorMessage.value = t('Please enter your username')
    return
  }

  try {
    isLoading.value = true
    errorMessage.value = ''
    successMessage.value = ''

    log('ConfirmPage', 'resendCode', 'Resending verification code', {
      username: username.value
    })

    const result = await resendConfirmationCode(username.value)

    if (result.success) {
      log('ConfirmPage', 'resendCode', '✅ Code resent successfully')
      successMessage.value = t('Verification code resent! Check your email.')
    } else {
      log('ConfirmPage', 'resendCode', '❌ Resend failed:', result.error)
      errorMessage.value = result.error || t('Failed to resend code')
    }
  } catch (error) {
    log('ConfirmPage', 'resendCode', '❌ Resend error:', error)
    errorMessage.value = t('Failed to resend code. Please try again.')
  } finally {
    isLoading.value = false
  }
}

/**
 * Navigate to login page
 */
function goToLogin(): void {
  const redirectQuery = route.query.redirectTo
  const redirectTo = typeof redirectQuery === 'string' ? redirectQuery : '/'

  router.push({
    path: '/login',
    query: { redirectTo }
  })
}
</script>

<style scoped>
.confirm-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: v-bind(primaryColor);
  padding: 1rem;
}

.confirm-container {
  width: 100%;
  max-width: 420px;
}

.confirm-card {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.confirm-logo {
  text-align: center;
  margin-bottom: 1.5rem;
}

.logo-image {
  max-width: 180px;
  max-height: 80px;
  height: auto;
  object-fit: contain;
}

.logo-placeholder {
  font-size: 1.5rem;
  font-weight: 700;
  color: v-bind(primaryColor);
  padding: 1rem;
}

.confirm-header {
  text-align: center;
  margin-bottom: 2rem;
}

.confirm-header h1 {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #718096;
  font-size: 0.875rem;
}

.confirm-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: #2d3748;
  font-size: 0.875rem;
}

.form-group input {
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: v-bind(primaryColor);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-group input:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
}

.help-text {
  color: #718096;
  font-size: 0.75rem;
  margin-top: -0.25rem;
}

.btn {
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: v-bind(secondaryColor);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-success {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
}

.btn-success:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-block {
  width: 100%;
}

.alert {
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
}

.alert-error {
  background-color: #fed7d7;
  color: #c53030;
  border: 1px solid #fc8181;
}

.alert-success {
  background-color: #c6f6d5;
  color: #22543d;
  border: 1px solid #68d391;
}

.confirm-footer {
  margin-top: 1.5rem;
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.confirm-footer p {
  color: #718096;
  font-size: 0.875rem;
}

.resend-link {
  color: v-bind(primaryColor);
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
}

.resend-link:hover {
  color: v-bind(secondaryColor);
  text-decoration: underline;
}
</style>
